import { phaseForSize, seedOrder, type Match, type MatchPhase } from '@brochure/shared'

/**
 * Un incontro del tabellone: squadre note (o `null` finché non lo sono) e la partita reale, se esiste.
 * `homeSeed`/`awaySeed`: nell'anteprima del primo turno, il piazzamento nel girone che occuperà quel posto.
 */
export type BracketSlot = { homeTeamId: string | null; awayTeamId: string | null; match?: Match; homeSeed?: number; awaySeed?: number }

/** Un turno del tabellone: `preview` è vero finché il turno non ha ancora partite reali create. */
export type BracketRound = { phase: MatchPhase; preview: boolean; matches: BracketSlot[]; thirdPlace?: BracketSlot }

/**
 * Tabellone a eliminazione diretta. Finché un turno non ha partite reali, gli accoppiamenti sono previsti:
 * il primo turno per piazzamento nel girone (1ª contro ultima qualificata, …), i successivi restano da definire.
 * I nomi non si prevedono: il primo turno nasce insieme alla fine del girone, prima la classifica non è quella vera.
 * La finalina (`third_place`), quando il formato la prevede (cioè quando esiste una semifinale), si affianca alla finale.
 */
export function buildRounds(matches: readonly Match[], advancing: number): BracketRound[] {
  const knockout = matches.filter((m) => m.phase !== 'group' && m.phase !== 'third_place')
  const thirdPlaceMatch = matches.find((m) => m.phase === 'third_place')

  const rounds: BracketRound[] = []
  // Con 2 squadre `advancingCount` vale 1 ma c'è comunque la finale diretta (senza girone): turno da 2.
  let size = Math.max(advancing, 2)
  const seeds = seedOrder(size)
  let isFirstRound = true
  while (size >= 2) {
    const phase = phaseForSize(size)
    const realMatches = knockout.filter((m) => m.phase === phase).sort((a, b) => a.sortOrder - b.sortOrder)
    const preview = realMatches.length === 0
    const slots: BracketSlot[] = !preview
      ? realMatches.map((m) => ({ homeTeamId: m.homeTeamId, awayTeamId: m.awayTeamId, match: m }))
      : Array.from({ length: size / 2 }, (_, i) => ({
          homeTeamId: null,
          awayTeamId: null,
          ...(isFirstRound && { homeSeed: seeds[i * 2], awaySeed: seeds[i * 2 + 1] }),
        }))
    rounds.push({ phase, preview, matches: slots })
    isFirstRound = false
    size /= 2
  }

  // La finalina esiste solo quando il formato passa da una semifinale (turno con 4 squadre): con un
  // tabellone a finale diretta (2 squadre in tutto) non c'è nulla da cui farla nascere.
  if (rounds.length >= 2) {
    const finalRound = rounds[rounds.length - 1]
    finalRound.thirdPlace = thirdPlaceMatch
      ? { homeTeamId: thirdPlaceMatch.homeTeamId, awayTeamId: thirdPlaceMatch.awayTeamId, match: thirdPlaceMatch }
      : { homeTeamId: null, awayTeamId: null }
  }

  return rounds
}
